import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Lang = 'nl' | 'en';

interface LangContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LangContext = createContext<LangContextType>({ lang: 'nl', setLang: () => {} });

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      return (localStorage.getItem('borca-lang') as Lang) || 'nl';
    } catch {
      return 'nl';
    }
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem('borca-lang', l);
    } catch {}
  }, []);

  return React.createElement(LangContext.Provider, { value: { lang, setLang } }, children);
}

export function useLang() {
  return useContext(LangContext);
}

const T: Record<string, { nl: string; en: string }> = {
  'tile.lezing': { nl: 'KORTE TERMIJN', en: 'THE READING' },
  'tile.lezing.layer': { nl: 'Komende uren', en: 'MICRO 0-4h' },
  'tile.stroom': { nl: 'GELDSTROOM', en: 'THE FLOW' },
  'tile.stroom.layer': { nl: 'Vandaag', en: 'MESO 4-12h' },
  'tile.analyse': { nl: 'ANALYSE', en: 'THE ANALYSIS' },
  'tile.analyse.layer': { nl: 'Komende dagen', en: 'MACRO 12-72h' },
  'tile.brein': { nl: 'TOTAALBEELD', en: 'THE BRAIN' },

  'dir.neutraal': { nl: 'AFWACHTEND', en: 'NEUTRAL' },
  'dir.bullish': { nl: 'POSITIEF', en: 'BULLISH' },
  'dir.bearish': { nl: 'NEGATIEF', en: 'BEARISH' },

  'momentum.sterkStijgend': { nl: 'Flink omhoog', en: 'STRONG RISING' },
  'momentum.stijgend': { nl: 'Licht omhoog', en: 'RISING' },
  'momentum.sterkDalend': { nl: 'Flink omlaag', en: 'STRONG FALLING' },
  'momentum.dalend': { nl: 'Licht omlaag', en: 'FALLING' },
  'momentum.stabiel': { nl: 'Rustig', en: 'STABLE' },

  'conf.hoog': { nl: 'Sterk signaal', en: 'HIGH' },
  'conf.midden': { nl: 'Gemiddeld signaal', en: 'MEDIUM' },
  'conf.laag': { nl: 'Zwak signaal', en: 'LOW' },

  'cross.sync': { nl: 'In lijn', en: 'ALIGNED' },
  'cross.divergent': { nl: 'Tegenstrijdig', en: 'DIVERGENT' },
  'cross.gemengd': { nl: 'Gemengd', en: 'MIXED' },

  'label.stabiliteit': { nl: 'Stabiliteit', en: 'STABILITY' },
  'label.engines': { nl: 'analyses', en: 'ENGINES' },
  'label.sync': { nl: 'eens', en: 'SYNC' },

  'nav.derivatives': { nl: 'Marktdata', en: 'DERIVATIVES' },
  'nav.liquidatie': { nl: 'Marktdruk', en: 'LIQUIDATION' },
  'nav.whales': { nl: 'Grote spelers', en: 'WHALES' },
  'nav.analist': { nl: 'ANALIST', en: 'ANALYST' },
  'nav.pro': { nl: 'PRO', en: 'PRO' },

  'header.transitie': { nl: 'Verschuiving', en: 'TRANSITION' },
  'header.loading': { nl: 'Laden...', en: 'LOADING' },

  'footer.live': { nl: 'LIVE', en: 'LIVE' },
  'footer.brand': { nl: 'HYPER-ORACLE', en: 'HYPER-ORACLE' },
  'footer.bearish': { nl: 'De markt toont een dalende trend', en: 'Regime: Bearish trend detected' },
  'footer.bullish': { nl: 'De markt toont positief momentum', en: 'Regime: Bullish momentum active' },
  'footer.range': { nl: 'De markt beweegt zijwaarts', en: 'Regime: Consolidation phase' },

  'overlay.micro.title': { nl: 'Korte Termijn', en: 'THE READING' },
  'overlay.micro.footer': { nl: 'Komende uren', en: 'MICRO HORIZON · 0-4 HOURS' },
  'overlay.meso.title': { nl: 'Geldstroom', en: 'THE FLOW' },
  'overlay.meso.footer': { nl: 'Vandaag', en: 'MESO HORIZON · 4-12 HOURS' },
  'overlay.macro.title': { nl: 'Analyse', en: 'THE ANALYSIS' },
  'overlay.macro.footer': { nl: 'Komende dagen', en: 'MACRO HORIZON · 12-72 HOURS' },
  'overlay.brein.title': { nl: 'Totaalbeeld', en: 'B·ORCA REPORT' },
  'overlay.brein.chapter': { nl: 'Samenvatting', en: 'B·ORCA INTELLIGENCE REPORT' },

  'hint.scenarios': { nl: "SCENARIO'S", en: 'SCENARIOS' },
  'hint.omdat': { nl: 'Omdat', en: 'Because' },
  'hint.kapitaalstroom': { nl: 'Geldstroom', en: 'CAPITAL FLOW' },
  'hint.drukpunten': { nl: 'Spanningspunten', en: 'PRESSURE POINTS' },
  'hint.momentum': { nl: 'Vaart', en: 'MOMENTUM' },
  'hint.horizons': { nl: 'Tijdshorizons', en: 'HORIZONS' },
  'hint.risicos': { nl: "RISICO'S", en: 'RISKS' },
  'hint.actie': { nl: 'Wat betekent dit', en: 'ACTION' },
  'hint.onChain': { nl: 'Blockchain', en: 'ON-CHAIN' },
  'hint.optiesEtf': { nl: 'Beleggingsproducten', en: 'OPTIONS & ETF' },
  'hint.correlaties': { nl: 'Verbanden', en: 'CORRELATIONS' },
  'hint.hetVerhaal': { nl: 'HET VERHAAL', en: 'THE STORY' },
  'hint.anticipatie': { nl: 'Vooruitblik', en: 'ANTICIPATION' },
  'hint.watKanBreken': { nl: 'WAT KAN DIT BREKEN', en: 'WHAT COULD BREAK THIS' },
  'hint.dejaVu': { nl: 'Historisch patroon', en: 'DEJA VU' },
  'hint.caiusIntelligence': { nl: 'Strategie', en: 'CAIUS INTELLIGENCE' },
  'hint.truthEngine': { nl: 'Waarheid', en: 'TRUTH ENGINE' },
  'hint.eliteVoting': { nl: 'Consensus', en: 'ELITE VOTING' },
  'hint.observerClass': { nl: 'Waarnemer', en: 'OBSERVER CLASS' },
  'hint.macro': { nl: 'MACRO', en: 'MACRO' },
  'hint.cyclus': { nl: 'Cyclus', en: 'CYCLE' },

  'ladder.weerstand': { nl: 'Plafond', en: 'RESISTANCE' },
  'ladder.huidigePrijs': { nl: 'HUIDIGE PRIJS', en: 'CURRENT PRICE' },
  'ladder.steun': { nl: 'Bodem', en: 'SUPPORT' },
  'ladder.liqMagneet': { nl: 'Aantrekkingspunt', en: 'LIQ. MAGNET' },

  'align.uitgelijnd': { nl: 'UITGELIJND', en: 'ALIGNED' },
  'align.divergent': { nl: 'Tegenstrijdig', en: 'DIVERGENT' },
  'align.gemengd': { nl: 'Gemengd', en: 'MIXED' },
  'align.uitgelijnd.desc': { nl: 'Alle tijdshorizons wijzen dezelfde kant op', en: 'All horizons point the same direction' },
  'align.divergent.desc': { nl: 'Korte en lange termijn spreken elkaar tegen', en: 'Horizons not aligned, wait for convergence' },
  'align.gemengd.desc': { nl: 'De signalen zijn gemengd, wacht op meer duidelijkheid', en: 'Horizons divided, stay neutral until clarity' },

  'narrative.verloor': { nl: 'verloor', en: 'lost' },
  'narrative.steeg': { nl: 'steeg', en: 'rose' },
  'narrative.bitcoin': { nl: 'Bitcoin', en: 'Bitcoin' },
  'narrative.terwijlDeMarktIn': { nl: 'terwijl de markt in', en: 'while the market is in' },
  'narrative.verkeert': { nl: 'verkeert', en: 'mode' },
  'narrative.koopmomentumDomineert': { nl: 'kopers hebben de overhand', en: 'buy momentum dominates' },
  'narrative.verkoopdrukDomineert': { nl: 'verkopers hebben de overhand', en: 'sell pressure dominates' },
  'narrative.kopersEnVerkopers': { nl: 'kopers en verkopers in balans', en: 'buyers and sellers in balance' },
  'narrative.verhoogd': { nl: 'boven gemiddeld', en: 'elevated, institutional activity' },
  'narrative.onderGemiddelde': { nl: 'onder gemiddelde', en: 'below average' },
  'narrative.rondGemiddelde': { nl: 'rond gemiddelde', en: 'around average' },
  'narrative.volume': { nl: 'volume', en: 'volume' },

  'narrative.liqLongDominant': { nl: 'kopers onder druk, kettingreactie mogelijk', en: 'long-dominant, cascade risk present' },
  'narrative.liqShortDominant': { nl: 'verkopers onder druk, omslag mogelijk', en: 'short-dominant, short squeeze possible' },
  'narrative.extremeAngst': { nl: 'Extreme angst in de markt — historisch gezien vaak een keerpunt', en: 'Extreme fear in the market, contrarian buy opportunity but timing uncertain' },
  'narrative.extremeHebzucht': { nl: 'Extreme hebzucht in de markt — historisch gezien een waarschuwingssignaal', en: 'Extreme greed, caution advised' },
  'narrative.momentumIntact': { nl: 'in 24 uur, de trend houdt aan', en: 'in 24h, momentum intact' },
  'narrative.correctieActief': { nl: 'in 24 uur, terugval gaande', en: 'in 24h, correction active' },
  'narrative.prijsDichterSteun': { nl: 'Prijs dichter bij de bodem dan het plafond', en: 'Price closer to support than resistance' },
  'narrative.prijsDichterWeerstand': { nl: 'Prijs dichter bij het plafond dan de bodem', en: 'Price closer to resistance than support' },
  'narrative.cascadeRisico': { nl: 'risico op kettingreactie', en: 'cascade risk present' },
  'narrative.marktGeanalyseerd': { nl: 'Marktcondities worden geanalyseerd', en: 'Market conditions are being analyzed' },

  'narrative.marktregime': { nl: 'Marktfase', en: 'Market regime' },
  'narrative.funding': { nl: 'Marktkosten', en: 'Funding' },
  'narrative.positief': { nl: 'positief', en: 'positive' },
  'narrative.negatief': { nl: 'negatief', en: 'negative' },
  'narrative.neutraal': { nl: 'neutraal', en: 'neutral' },
  'narrative.longDominant': { nl: 'kopers dominant', en: 'long-dominant' },
  'narrative.shortDominant': { nl: 'verkopers dominant', en: 'short-dominant' },
  'narrative.gebalanceerde': { nl: 'gebalanceerde', en: 'balanced' },
  'narrative.positionering': { nl: 'positieopbouw', en: 'positioning' },

  'narrative.oiStijgt': { nl: 'Meer posities geopend', en: 'Open Interest rising' },
  'narrative.oiDaalt': { nl: 'Posities worden gesloten', en: 'Open Interest falling' },
  'narrative.in4u': { nl: 'in 4u', en: 'in 4h' },
  'narrative.versKapitaalLong': { nl: 'Nieuw geld stroomt de markt in aan de koopkant', en: 'Fresh capital flowing into long positions' },
  'narrative.aggressieveShort': { nl: 'Verkopers bouwen agressief posities op', en: 'Aggressive short-building' },
  'narrative.positiesGesloten': { nl: 'Posities worden gesloten', en: 'Positions are being closed' },

  'narrative.cascadeRisicoDrukpunt': { nl: 'Risico op kettingreactie', en: 'Cascade risk' },
  'narrative.intensiteit': { nl: 'intensiteit', en: 'intensity' },

  'narrative.fundingIs': { nl: 'Marktkosten zijn', en: 'Funding is' },
  'narrative.openInterest': { nl: 'Aantal posities', en: 'Open interest' },
  'narrative.lsRatio': { nl: 'Kopers vs verkopers', en: 'L/S ratio at' },
  'narrative.fase': { nl: 'fase', en: 'phase' },

  'narrative.longLiq1u': { nl: 'Gedwongen verkopen kopers (1u)', en: 'Long liquidations 1h' },
  'narrative.shortLiq1u': { nl: 'Gedwongen verkopen verkopers (1u)', en: 'short liquidations 1h' },
  'narrative.totaal24u': { nl: 'totaal 24u', en: 'total 24h' },
  'narrative.liqMagneet': { nl: 'Aantrekkingspunt', en: 'Liquidation magnet' },
  'narrative.op': { nl: 'op', en: 'at' },
  'narrative.afstand': { nl: 'afstand', en: 'distance' },
  'narrative.longDominantie': { nl: 'Kopers dominant', en: 'Long dominance' },

  'narrative.sterk': { nl: 'STERK', en: 'STRONG' },
  'narrative.matig': { nl: 'MATIG', en: 'MODERATE' },
  'narrative.zwak': { nl: 'ZWAK', en: 'WEAK' },

  'narrative.marktOpereertIn': { nl: 'De markt bevindt zich in', en: 'Market operating in' },
  'narrative.regime': { nl: 'fase', en: 'regime' },
  'narrative.risico': { nl: 'risico', en: 'risk' },

  'meso.breakBovenMetVolume': { nl: 'Doorbraak boven', en: 'Break above' },
  'meso.metVolume': { nl: 'met volume', en: 'with volume' },
  'meso.sustainedMoveBoven': { nl: 'Aanhoudende stijging boven het plafond', en: 'Sustained move above resistance' },
  'meso.breakOnder': { nl: 'Doorbraak onder', en: 'Break below' },
  'meso.metToenemendVolume': { nl: 'met toenemend volume', en: 'with increasing volume' },
  'meso.lossVanSteun': { nl: 'Verlies van bodem', en: 'Loss of support' },
  'meso.shortSqueeze': { nl: 'Verkopers worden gedwongen hun posities te sluiten', en: 'Short squeeze with negative funding + volume spike' },
  'meso.squeezeUitleg': { nl: 'Potentieel voor een omslag door eenzijdige marktopbouw', en: 'Squeeze potential from one-sided positioning' },
  'meso.accumulatieBevestigd': { nl: 'Grote beleggers bouwen posities op, zichtbaar in groeiende marktactiviteit', en: 'Accumulation confirmed by rising OI + price' },
  'meso.versKapitaalBevestigt': { nl: 'Nieuw geld bevestigt de koopkracht', en: 'Fresh capital confirms buying power' },
  'meso.longLiqCascade': { nl: 'Golf van gedwongen verkopen mogelijk onder de bodem', en: 'Long liquidation cascade below support' },
  'meso.eenzijdigeLong': { nl: 'Te veel kopers aan dezelfde kant, dat kan gevaarlijk worden', en: 'One-sided long positioning as fuel for decline' },
  'meso.distributieFase': { nl: 'Grote beleggers verkopen hun posities, de markt wordt rustiger', en: 'Distribution phase: declining OI with falling price' },
  'meso.kapitaalVerlaat': { nl: 'Geld stroomt uit de markt', en: 'Capital leaving the market' },
  'meso.geenExtremeRisicos': { nl: 'Geen extreme risicofactoren in de kapitaalstroom.', en: 'No extreme risk factors in the capital flow.' },
  'meso.extremeFunding': { nl: 'Extreme marktkosten, te veel hefboom verhoogt het risico op een snelle terugval.', en: 'Extreme funding, overleveraging increases risk of quick correction.' },

  'brein.vorigeCall': { nl: 'Vorige voorspelling', en: 'Previous call' },
  'brein.correct': { nl: 'CORRECT', en: 'CORRECT' },
  'brein.incorrect': { nl: 'INCORRECT', en: 'INCORRECT' },
  'brein.trackRecord': { nl: 'Trefzekerheid', en: 'Track record' },
  'brein.metaAccuratesse': { nl: 'Nauwkeurigheid systeem', en: 'System accuracy' },
  'brein.match': { nl: 'MATCH', en: 'MATCH' },
  'brein.kans': { nl: 'Kans', en: 'Probability' },
  'brein.checkpoint': { nl: 'Controle op', en: 'Checkpoint at' },
  'brein.momentumTrend': { nl: 'Vaart', en: 'Momentum' },
  'brein.onbekend': { nl: 'onbekend', en: 'unknown' },
  'brein.smartMoney': { nl: 'Grote beleggers', en: 'Smart Money' },
  'brein.aligned': { nl: 'Eensgezind', en: 'Aligned' },
  'brein.nietAligned': { nl: 'Verdeeld', en: 'Not aligned' },
  'brein.gemMove': { nl: 'Gem. prijsbeweging', en: 'Avg. move' },
  'brein.winRate': { nl: 'Winstkans', en: 'Win rate' },
  'brein.advies': { nl: 'inzicht', en: 'advice' },
  'brein.vetoActief': { nl: 'WAARSCHUWING ACTIEF', en: 'VETO ACTIVE' },
  'brein.voting': { nl: 'stemming', en: 'voting' },
  'brein.vetoEligible': { nl: 'kan waarschuwen', en: 'veto-eligible' },
  'brein.whale': { nl: 'Grote spelers', en: 'Whales' },
  'brein.structureel': { nl: 'Structureel', en: 'Structural' },
  'brein.breuk': { nl: 'BREUK', en: 'BREAK' },
  'brein.stabiel': { nl: 'STABIEL', en: 'STABLE' },
  'brein.keerpunten': { nl: 'KEERPUNTEN', en: 'TRIGGERS' },
  'brein.neiging': { nl: 'Richting', en: 'Bias' },
  'brein.systeemSynthese': { nl: 'SAMENVATTING', en: 'SYSTEM SYNTHESIS' },
  'brein.signalen': { nl: 'SIGNALEN', en: 'SIGNALS' },
  'brein.overeenstemming': { nl: 'overeenstemming', en: 'consensus' },
  'brein.analyseMotoren': { nl: 'analyses', en: 'engines' },
  'brein.samenvatting': { nl: 'SAMENVATTING', en: 'SUMMARY' },
  'brein.doel': { nl: 'DOEL', en: 'TARGET' },
  'brein.ommekeer': { nl: 'OMMEKEER', en: 'FLIP' },
  'brein.geenPositieUitleg': { nl: 'Onze analyses zijn verdeeld — er is nog geen duidelijk beeld', en: 'NO POSITION — Insufficient consensus. Engines are divided.' },
  'brein.volgendeEvaluatie': { nl: 'Volgende update', en: 'Next evaluation' },
  'brein.overeenkomst': { nl: 'OVEREENKOMST', en: 'MATCH' },
  'brein.risicoVerhouding': { nl: 'Risico', en: 'R:R' },

  'vizier.loading': { nl: 'Even geduld...', en: 'BUILDING ANALYSIS' },
  'vizier.detectie': { nl: 'WAT ZIEN WE', en: 'DETECTION' },
  'vizier.magneetveld': { nl: 'WAAR GAAT DE PRIJS HEEN', en: 'MAGNETIC FIELD' },
  'vizier.marktfase': { nl: 'WELKE FASE', en: 'MARKET PHASE' },
  'vizier.geheugen': { nl: 'WAT VERANDERDE', en: 'MEMORY' },
  'vizier.risico': { nl: 'WAAR MOET JE OP LETTEN', en: 'RISK' },
  'vizier.temp.heet': { nl: 'HEET', en: 'HOT' },
  'vizier.temp.warm': { nl: 'WARM', en: 'WARM' },
  'vizier.temp.koel': { nl: 'KOEL', en: 'COOL' },
  'vizier.temp.koud': { nl: 'KOUD', en: 'COLD' },
  'vizier.chain.omdat': { nl: 'WANT', en: 'BECAUSE' },
  'vizier.chain.daarom': { nl: 'DUS', en: 'THEREFORE' },
  'vizier.chain.verwacht': { nl: 'VOORUITBLIK', en: 'EXPECT' },

  'tier.waarnemer': { nl: 'WAARNEMER', en: 'OBSERVER' },
  'tier.analist': { nl: 'ANALIST', en: 'ANALYST' },
  'tier.institutioneel': { nl: 'INSTITUTIONEEL', en: 'INSTITUTIONAL' },
  'tier.ontgrendel': { nl: 'ONTGRENDEL', en: 'UNLOCK' },
  'tier.mesoDynamisch': { nl: 'Uitgebreide Analyse', en: 'Dynamic Intelligence' },
  'tier.crossLayer': { nl: 'Diepere Inzichten', en: 'Cross-Layer Intelligence' },

  'page.betalingGeslaagd': { nl: 'BETALING GESLAAGD', en: 'PAYMENT SUCCESSFUL' },
  'page.abonnementActief': { nl: 'abonnement is nu actief', en: 'subscription is now active' },
  'page.doorgestuurd': { nl: 'Je wordt automatisch doorgestuurd...', en: 'You will be redirected automatically...' },

  'page.trackrecord.title': { nl: 'B\u00B7ORCA CERTIFIED', en: 'B\u00B7ORCA CERTIFIED' },
  'page.trackrecord.subtitle': { nl: 'Bewezen backtest engines met \u226580% accuracy op 80.191 candles', en: 'Proven backtest engines with \u226580% accuracy on 80,191 candles' },
  'page.trackrecord.f1': { nl: 'Gecertificeerde engines per familie', en: 'Certified engines by family' },
  'page.trackrecord.f2': { nl: 'Live activatiestatus', en: 'Live activation status' },
  'page.trackrecord.f3': { nl: 'Accuracy ranking met bewijs', en: 'Accuracy ranking with proof' },
  'page.trackrecord.f4': { nl: 'Horizon verdeling (1h/4h/24h)', en: 'Horizon distribution (1h/4h/24h)' },
  'page.trackrecord.f5': { nl: 'Consensus richting', en: 'Consensus direction' },
  'page.trackrecord.f6': { nl: 'Methodologie en validatie', en: 'Methodology and validation' },

  'page.derivatives.title': { nl: 'MARKTDATA', en: 'DERIVATIVES LAB' },
  'page.derivatives.subtitle': { nl: 'Live marktdata en analyse', en: 'Real-time derivatives analytics for serious traders' },
  'page.derivatives.f1': { nl: 'Hoeveel posities zijn er open?', en: 'Open Interest tracking with trends' },
  'page.derivatives.f2': { nl: 'Wat kost het om een positie aan te houden?', en: 'Funding Rate analysis + annualized' },
  'page.derivatives.f3': { nl: 'Verhouding kopers en verkopers', en: 'Long/Short Ratio with reversal signals' },
  'page.derivatives.f4': { nl: 'Gedwongen verkopen en kettingreactierisico', en: 'Liquidation overview + cascade risk' },
  'page.derivatives.f5': { nl: 'Optiemarkt analyse', en: 'Options Intelligence (Put/Call, Max Pain)' },
  'page.derivatives.f6': { nl: 'Marktsentiment', en: 'Market Sentiment gauge' },

  'page.liquidation.title': { nl: 'MARKTDRUK', en: 'LIQUIDATION HEATMAP' },
  'page.liquidation.subtitle': { nl: 'Waar liggen de grootste risicozones?', en: 'Professional liquidation analysis with zone mapping' },
  'page.liquidation.f1': { nl: 'Risicozones per hefboomniveau', en: 'Complete liquidation heatmap per leverage' },
  'page.liquidation.f2': { nl: 'Kettingreactie-analyse', en: 'Cascade risk analysis' },
  'page.liquidation.f3': { nl: 'Hefboomverdeling', en: 'Leverage distribution overview' },
  'page.liquidation.f4': { nl: 'Live drukmeter', en: 'Real-time liquidation pressure indicator' },
  'page.liquidation.f5': { nl: 'Kopers vs verkopers druk', en: 'Long vs Short liquidation breakdown' },
  'page.liquidation.f6': { nl: 'Aantrekkingspunt detectie', en: 'Magnet price detection' },

  'page.whales.title': { nl: 'GROTE SPELERS', en: 'WHALE TRACKER' },
  'page.whales.subtitle': { nl: 'Volg wat de grote beleggers doen', en: 'Track smart money movements in real-time' },
  'page.whales.f1': { nl: 'Koop- en verkoopdruk', en: 'Pressure Index gauge (buy/sell pressure)' },
  'page.whales.f2': { nl: 'Saldo op handelsplatformen', en: 'Exchange balance tracking' },
  'page.whales.f3': { nl: 'Kopen of verkopen de grote spelers?', en: 'Whale accumulation/distribution signals' },
  'page.whales.f4': { nl: 'Aantrekkingspunt analyse', en: 'Liquidation magnet analysis' },
  'page.whales.f5': { nl: 'Positie-overzicht', en: 'Derivatives position overview' },
  'page.whales.f6': { nl: 'Marktoverzicht', en: 'Market intelligence dashboard' },

  'tier.light.highlight': { nl: 'Geldstroom, Technische Analyse, Marktdata', en: 'Meso Horizon, Bollinger Bands, Derivatives Lab' },
  'tier.light.f1': { nl: 'Analyse over meerdere tijdshorizons', en: 'Multi-timeframe analysis' },
  'tier.light.f2': { nl: 'Slimme verhaalanalyse', en: 'Narrative intelligence' },
  'tier.light.f3': { nl: 'Track Record', en: 'Track Record' },
  'tier.premium.highlight': { nl: 'Grote Spelers, Marktdruk, 27 Analyses', en: 'Whale Tracker, Liquidation Heatmap, 27 Engines' },
  'tier.premium.f1': { nl: 'AI Redenering', en: 'AI Reasoning (Grok + GPT)' },
  'tier.premium.f2': { nl: 'Live waarschuwingen', en: 'Real-time SSE alerts' },
  'tier.premium.f3': { nl: 'Alle analyses ontgrendeld', en: 'Full engine intelligence' },

  'macro.stablecoinFlow': { nl: 'Stablecoin flow', en: 'Stablecoin flow' },
  'macro.instroom': { nl: 'INSTROOM', en: 'INFLOW' },
  'macro.uitstroom': { nl: 'UITSTROOM', en: 'OUTFLOW' },
  'macro.miners': { nl: 'Miners', en: 'Miners' },
  'macro.capitulatie': { nl: 'CAPITULATIE', en: 'CAPITULATION' },
  'macro.stabiel': { nl: 'STABIEL', en: 'STABLE' },
  'macro.cycleScore': { nl: 'Cyclus score', en: 'Cycle score' },

  'fg.extremeAngst': { nl: 'extreme angst', en: 'extreme fear' },
  'fg.angst': { nl: 'angst', en: 'fear' },
  'fg.extremeHebzucht': { nl: 'extreme hebzucht', en: 'extreme greed' },
  'fg.hebzucht': { nl: 'hebzucht', en: 'greed' },
  'fg.neutraal': { nl: 'neutraal', en: 'neutral' },

  'regime.bullishTrend': { nl: 'Stijgende Trend', en: 'Bullish Trend' },
  'regime.bearishTrend': { nl: 'Dalende Trend', en: 'Bearish Trend' },
  'regime.rangeBound': { nl: 'Zijwaarts', en: 'Range-Bound' },
  'regime.hogeVolatiliteit': { nl: 'Hoge Volatiliteit', en: 'High Volatility' },

  'macro.onChain.exchangeReserves': { nl: 'Exchange reserves', en: 'Exchange reserves' },
  'macro.trendLabel': { nl: 'trend', en: 'trend' },

  'label.analyseren': { nl: 'ANALYSEREN', en: 'ANALYZING' },
  'narrative.stijgt': { nl: 'stijgt', en: 'rising' },
  'narrative.daalt': { nl: 'daalt', en: 'falling' },
  'narrative.liquidaties': { nl: 'Liquidaties', en: 'Liquidations' },
  'meso.longLiqDomineren': { nl: 'Kopers worden het hardst geraakt', en: 'Long liquidations dominate' },
  'meso.cascadeRisicoBijDaling': { nl: 'risico op kettingreactie bij prijsdaling', en: 'cascade risk with price drop' },
  'meso.exchangeBalans': { nl: 'Saldo op handelsplatformen', en: 'Exchange balance' },
  'meso.hashrateIs': { nl: 'Rekenkracht netwerk is', en: 'Hashrate is' },
  'meso.etfFlowsDag': { nl: 'Beleggingsfonds instroom vandaag', en: 'ETF flows day' },
  'meso.week': { nl: 'week', en: 'week' },
  'brein.cyclusFase': { nl: 'Markfase', en: 'Cycle phase' },
  'brein.geenVeto': { nl: 'GEEN VETO', en: 'NO VETO' },
  'brein.actief': { nl: 'ACTIEF', en: 'ACTIVE' },
  'brein.uit': { nl: 'UIT', en: 'OFF' },
  'brein.observaties': { nl: 'observaties', en: 'observations' },
  'brein.frozenByObserver': { nl: 'GEPAUZEERD', en: 'FROZEN BY OBSERVER' },
  'brein.confidenceTeLaag': { nl: 'Vertrouwen te laag voor een duidelijk signaal', en: 'Confidence too low to trade' },
  'brein.geenPositie': { nl: 'Onze analyses zijn verdeeld — er is nog geen duidelijk beeld', en: 'NO POSITION — Insufficient consensus for directional trade. Engines divided.' },
  'brein.engineConsensus': { nl: 'HOE EENS ZIJN WE', en: 'ENGINE CONSENSUS' },
  'hint.quantIntelligence': { nl: 'ANALYSE', en: 'QUANT INTELLIGENCE' },
  'time.0_4': { nl: '0-4u', en: '0-4h' },
  'time.4_12': { nl: '4-12u', en: '4-12h' },
  'time.12_72': { nl: '12-72u', en: '12-72h' },
  'time.1u': { nl: '1u', en: '1h' },
  'time.4u': { nl: '4u', en: '4h' },
  'time.24u': { nl: '24u', en: '24h' },

  'dashboard.loadFailed': { nl: 'Laden mislukt', en: 'Loading failed' },
  'dashboard.close': { nl: 'Sluiten', en: 'Close' },
  'dashboard.daglezing': { nl: 'Daglezing', en: 'Daily Reading' },
  'dashboard.direction': { nl: 'MARKTRICHTING', en: 'MARKET DIRECTION' },
  'dashboard.keyMetrics': { nl: 'KERNCIJFERS', en: 'KEY METRICS' },
  'dashboard.strategy': { nl: 'STRATEGIE', en: 'STRATEGY' },
  'dashboard.total': { nl: 'Totaal', en: 'Total' },
  'dashboard.dir.up': { nl: 'STIJGING', en: 'RISING' },
  'dashboard.dir.down': { nl: 'DALING', en: 'FALLING' },
  'dashboard.dir.neutral': { nl: 'NEUTRAAL', en: 'NEUTRAL' },

  'vizier.brein.title': { nl: 'zelfredenerend brein', en: 'self-reasoning brain' },
  'vizier.brein.positive': { nl: 'positief', en: 'positive' },
  'vizier.brein.negative': { nl: 'negatief', en: 'negative' },
  'vizier.brein.mixed': { nl: 'gemengd', en: 'mixed' },
  'vizier.brein.lenses': { nl: 'lenzen', en: 'lenses' },
  'vizier.brein.justNow': { nl: 'zojuist', en: 'just now' },
  'vizier.brein.minAgo': { nl: 'min geleden', en: 'min ago' },
  'vizier.type.contradictie': { nl: 'tegenstrijdig', en: 'contradiction' },
  'vizier.type.correlatie': { nl: 'verband', en: 'correlation' },
  'vizier.type.anticipatie': { nl: 'verwachting', en: 'anticipation' },
  'vizier.type.hint': { nl: 'signaal', en: 'signal' },
  'vizier.type.regime': { nl: 'fase', en: 'phase' },
  'vizier.type.risico': { nl: 'risico', en: 'risk' },
  'vizier.type.momentum': { nl: 'momentum', en: 'momentum' },
  'vizier.type.structuur': { nl: 'structuur', en: 'structure' },
  'vizier.thinking.0': { nl: 'verbanden leggen...', en: 'connecting patterns...' },
  'vizier.thinking.1': { nl: 'patronen doorrekenen...', en: 'calculating patterns...' },
  'vizier.thinking.2': { nl: 'signalen correleren...', en: 'correlating signals...' },
  'vizier.thinking.3': { nl: 'risico evalueren...', en: 'evaluating risk...' },
  'vizier.thinking.4': { nl: 'momentum analyseren...', en: 'analyzing momentum...' },
  'vizier.thinking.5': { nl: 'scenario modelleren...', en: 'modeling scenarios...' },
  'vizier.thinking.6': { nl: 'marktstructuur doorlichten...', en: 'scanning market structure...' },
  'vizier.thinking.7': { nl: 'divergenties scannen...', en: 'scanning divergences...' },
  'vizier.thinking.8': { nl: 'liquidatiezones mappen...', en: 'mapping liquidation zones...' },
  'vizier.thinking.9': { nl: 'consensus wegen...', en: 'weighing consensus...' },

  'whale.title': { nl: 'WHALE TRACKER', en: 'WHALE TRACKER' },
  'whale.subtitle': { nl: 'ON-CHAIN ANALYSE', en: 'ON-CHAIN ANALYSIS' },
  'whale.loading': { nl: 'WHALE DATA LADEN...', en: 'LOADING WHALE DATA...' },
  'whale.kernpunt': { nl: 'KERNPUNT', en: 'KEY POINT' },
  'whale.druk': { nl: 'DRUK', en: 'PRESSURE' },
  'whale.pattern': { nl: 'PATROON', en: 'PATTERN' },
  'whale.flow': { nl: 'FLOW', en: 'FLOW' },
  'whale.sentiment': { nl: 'SENTIMENT', en: 'SENTIMENT' },
  'whale.pressureIndex': { nl: 'DRUK INDEX', en: 'PRESSURE INDEX' },
  'whale.exchangeBalance': { nl: 'EXCHANGE BALANCE', en: 'EXCHANGE BALANCE' },
  'whale.onExchanges': { nl: 'OP EXCHANGES', en: 'ON EXCHANGES' },
  'whale.change7d': { nl: '7D VERANDERING', en: '7D CHANGE' },
  'whale.trend': { nl: 'TREND', en: 'TREND' },
  'whale.whaleBehavior': { nl: 'WHALE GEDRAG', en: 'WHALE BEHAVIOR' },
  'whale.strength': { nl: 'STERKTE', en: 'STRENGTH' },
  'whale.recentTx': { nl: 'RECENT TX', en: 'RECENT TX' },
  'whale.netFlow24h': { nl: 'NET FLOW 24H', en: 'NET FLOW 24H' },
  'whale.avgTxSize': { nl: 'GEM. TX GROOTTE', en: 'AVG TX SIZE' },
  'whale.largestTx': { nl: 'GROOTSTE TX', en: 'LARGEST TX' },
  'whale.exchangeFlows': { nl: 'EXCHANGE FLOWS', en: 'EXCHANGE FLOWS' },
  'whale.netExchangeFlow': { nl: 'NETTO EXCHANGE FLOW', en: 'NET EXCHANGE FLOW' },
  'whale.direction': { nl: 'RICHTING', en: 'DIRECTION' },
  'whale.intensity': { nl: 'INTENSITEIT', en: 'INTENSITY' },
  'whale.whaleSentiment': { nl: 'WHALE SENTIMENT', en: 'WHALE SENTIMENT' },
  'whale.confidence': { nl: 'CONFIDENCE', en: 'CONFIDENCE' },
  'whale.expectedImpact': { nl: 'VERWACHTE IMPACT', en: 'EXPECTED IMPACT' },
  'whale.signals': { nl: 'SIGNALEN', en: 'SIGNALS' },
  'whale.liqPressure': { nl: 'LIQUIDATIE DRUK', en: 'LIQUIDATION PRESSURE' },
  'whale.1hour': { nl: '1 UUR', en: '1 HOUR' },
  'whale.4hour': { nl: '4 UUR', en: '4 HOURS' },
  'whale.24hour': { nl: '24 UUR', en: '24 HOURS' },
  'whale.longShortLiq': { nl: 'LONG/SHORT LIQUIDATIES', en: 'LONG/SHORT LIQUIDATIONS' },
  'whale.cascade': { nl: 'CASCADE', en: 'CASCADE' },
  'whale.liqMagnet': { nl: 'LIQUIDATIE MAGNEET', en: 'LIQUIDATION MAGNET' },
  'whale.upward': { nl: 'OPWAARTS', en: 'UPWARD' },
  'whale.downward': { nl: 'NEERWAARTS', en: 'DOWNWARD' },
  'whale.liqAtMagnet': { nl: 'LIQUIDITY BIJ MAGNEET', en: 'LIQUIDITY AT MAGNET' },
  'whale.distToPrice': { nl: 'AFSTAND TOT PRIJS', en: 'DISTANCE TO PRICE' },
  'whale.upsideZones': { nl: 'UPSIDE ZONES', en: 'UPSIDE ZONES' },
  'whale.downsideZones': { nl: 'DOWNSIDE ZONES', en: 'DOWNSIDE ZONES' },
  'whale.derivPosition': { nl: 'DERIVATEN POSITIE', en: 'DERIVATIVES POSITION' },
  'whale.openInterest': { nl: 'OPEN INTEREST', en: 'OPEN INTEREST' },
  'whale.fundingRate': { nl: 'FUNDING RATE', en: 'FUNDING RATE' },
  'whale.longShortRatio': { nl: 'LONG/SHORT RATIO', en: 'LONG/SHORT RATIO' },
  'whale.putCallRatio': { nl: 'PUT/CALL RATIO', en: 'PUT/CALL RATIO' },
  'whale.longsDominant': { nl: 'Longs dominant', en: 'Longs dominant' },
  'whale.shortsDominant': { nl: 'Shorts dominant', en: 'Shorts dominant' },
  'whale.balanced': { nl: 'Gebalanceerd', en: 'Balanced' },
  'whale.callsDominant': { nl: 'Calls dominant', en: 'Calls dominant' },
  'whale.putsDominant': { nl: 'Puts dominant', en: 'Puts dominant' },
  'whale.neutralLabel': { nl: 'Neutraal', en: 'Neutral' },
  'whale.marketIntel': { nl: 'MARKT INTELLIGENTIE', en: 'MARKET INTELLIGENCE' },
  'whale.regimeLabel': { nl: 'REGIME', en: 'REGIME' },
  'whale.marketPhase': { nl: 'MARKTFASE', en: 'MARKET PHASE' },
  'whale.fearGreed': { nl: 'FEAR & GREED', en: 'FEAR & GREED' },
  'whale.stability': { nl: 'STABILITEIT', en: 'STABILITY' },
  'whale.hashrate7d': { nl: 'HASHRATE 7D', en: 'HASHRATE 7D' },
  'whale.minerStatus': { nl: 'MINER STATUS', en: 'MINER STATUS' },
  'whale.capitulation': { nl: 'CAPITULATIE', en: 'CAPITULATION' },
  'whale.stableStatus': { nl: 'STABIEL', en: 'STABLE' },
  'whale.cycleIntel': { nl: 'CYCLUS INTELLIGENTIE', en: 'CYCLE INTELLIGENCE' },
  'whale.patternsLearned': { nl: 'PATRONEN GELEERD', en: 'PATTERNS LEARNED' },
  'whale.historyDepth': { nl: 'HISTORIE DIEPTE', en: 'HISTORY DEPTH' },
  'whale.activeSteps': { nl: 'ACTIEVE STAPPEN', en: 'ACTIVE STEPS' },
  'whale.accumulationScore': { nl: 'ACCUMULATIE SCORE', en: 'ACCUMULATION SCORE' },
  'whale.strongAccumulating': { nl: 'STERK ACCUMULEREND', en: 'STRONG ACCUMULATING' },
  'whale.accumulating': { nl: 'ACCUMULEREND', en: 'ACCUMULATING' },
  'whale.distributing': { nl: 'DISTRIBUEREND', en: 'DISTRIBUTING' },
  'whale.strongDistributing': { nl: 'STERK DISTRIBUEREND', en: 'STRONG DISTRIBUTING' },
  'whale.distributionGauge': { nl: 'DISTRIBUTIE', en: 'DISTRIBUTION' },
  'whale.neutralGauge': { nl: 'NEUTRAAL', en: 'NEUTRAL' },
  'whale.accumulationGauge': { nl: 'ACCUMULATIE', en: 'ACCUMULATION' },
  'whale.unknown': { nl: 'ONBEKEND', en: 'UNKNOWN' },
  'whale.networkHealth': { nl: 'NETWERK GEZONDHEID', en: 'NETWORK HEALTH' },
  'whale.healthy': { nl: 'GEZOND', en: 'HEALTHY' },
  'whale.stressed': { nl: 'GESTREST', en: 'STRESSED' },
  'whale.critical': { nl: 'KRITIEK', en: 'CRITICAL' },
  'whale.degraded': { nl: 'VERSLECHTERD', en: 'DEGRADED' },
  'whale.sentimentFusion': { nl: 'SENTIMENT FUSIE', en: 'SENTIMENT FUSION' },
  'whale.strongBullish': { nl: 'STERK BULLISH', en: 'STRONG BULLISH' },
  'whale.strongBearish': { nl: 'STERK BEARISH', en: 'STRONG BEARISH' },
  'whale.whaleAction': { nl: 'WHALE ACTIE', en: 'WHALE ACTION' },
  'whale.altDataSignal': { nl: 'ALT DATA SIGNAAL', en: 'ALT DATA SIGNAL' },
  'whale.lastUpdate': { nl: 'LAATSTE UPDATE', en: 'LAST UPDATE' },
  'whale.trendAccumulation': { nl: 'ACCUMULATIE', en: 'ACCUMULATION' },
  'whale.trendDistribution': { nl: 'DISTRIBUTIE', en: 'DISTRIBUTION' },
  'whale.trendStable': { nl: 'STABIEL', en: 'STABLE' },
  'whale.buyPressure': { nl: 'KOOPDRUK', en: 'BUY PRESSURE' },
  'whale.lightBuyPressure': { nl: 'LICHTE KOOPDRUK', en: 'LIGHT BUY PRESSURE' },
  'whale.sellPressure': { nl: 'VERKOOPDRUK', en: 'SELL PRESSURE' },
  'whale.heavySellPressure': { nl: 'ZWARE VERKOOPDRUK', en: 'HEAVY SELL PRESSURE' },
  'whale.aiIntelligence': { nl: 'AI INTELLIGENTIE', en: 'AI INTELLIGENCE' },
  'whale.classification': { nl: 'CLASSIFICATIE', en: 'CLASSIFICATION' },
  'whale.synergy': { nl: 'SYNERGY', en: 'SYNERGY' },
  'whale.sellPressureScale': { nl: 'VERKOOPDRUK', en: 'SELL PRESSURE' },
  'whale.neutralScale': { nl: 'NEUTRAAL', en: 'NEUTRAL' },
  'whale.buyPressureScale': { nl: 'KOOPDRUK', en: 'BUY PRESSURE' },
  'whale.bearishGauge': { nl: 'BEARISH', en: 'BEARISH' },
  'whale.bullishGauge': { nl: 'BULLISH', en: 'BULLISH' },
  'whale.whaleActivity': { nl: 'WHALE ACTIVITEIT', en: 'WHALE ACTIVITY' },
  'whale.exBalChange': { nl: 'EXCHANGE BAL. CHANGE', en: 'EXCHANGE BAL. CHANGE' },
  'whale.socialVolume': { nl: 'SOCIAL VOLUME', en: 'SOCIAL VOLUME' },
  'whale.strongNeutral': { nl: 'NEUTRAAL', en: 'NEUTRAL' },
  'whale.strongNeutralAlt': { nl: 'STERK NEUTRAAL', en: 'STRONG NEUTRAL' },

  'liq.title': { nl: 'LIQUIDATIE HEATMAP', en: 'LIQUIDATION HEATMAP' },
  'liq.loading': { nl: 'DATA LADEN...', en: 'LOADING DATA...' },
  'liq.deathZoneActive': { nl: 'DEATH ZONE ACTIEF', en: 'DEATH ZONE ACTIVE' },
  'liq.deathZoneDefault': { nl: 'Extreem gevaarlijke marktcondities gedetecteerd', en: 'Extremely dangerous market conditions detected' },
  'liq.total24h': { nl: 'LIQ 24U TOTAAL', en: 'LIQ 24H TOTAL' },
  'liq.cascadeRisk': { nl: 'CASCADE RISICO', en: 'CASCADE RISK' },
  'liq.dominant': { nl: 'DOMINANT', en: 'DOMINANT' },
  'liq.pressure': { nl: 'DRUK', en: 'PRESSURE' },
  'liq.cascadeProximity': { nl: 'CASCADE PROXIMITEIT', en: 'CASCADE PROXIMITY' },
  'liq.maxPainGravity': { nl: 'MAX PAIN ZWAARTEKRACHT', en: 'MAX PAIN GRAVITY' },
  'liq.price': { nl: 'PRIJS', en: 'PRICE' },
  'liq.maxPain': { nl: 'MAX PAIN', en: 'MAX PAIN' },
  'liq.zoneIntel': { nl: 'LIQUIDATIE ZONE INTELLIGENTIE', en: 'LIQUIDATION ZONE INTELLIGENCE' },
  'liq.activeZones': { nl: 'ACTIEVE ZONES', en: 'ACTIVE ZONES' },
  'liq.nearest': { nl: 'DICHTSTBIJ', en: 'NEAREST' },
  'liq.magneticZones': { nl: 'MAGNETISCHE ZONES', en: 'MAGNETIC ZONES' },
  'liq.nearestLongLiq': { nl: 'DICHTSTE LONG LIQUIDATIE', en: 'NEAREST LONG LIQUIDATION' },
  'liq.nearestShortLiq': { nl: 'DICHTSTE SHORT LIQUIDATIE', en: 'NEAREST SHORT LIQUIDATION' },
  'liq.fromCurrentPrice': { nl: 'van huidige prijs', en: 'from current price' },
  'liq.magneticLevel': { nl: 'MAGNETISCH NIVEAU', en: 'MAGNETIC LEVEL' },
  'liq.oiLabel': { nl: 'OPEN INTEREST', en: 'OPEN INTEREST' },
  'liq.oi4h': { nl: 'OI 4U', en: 'OI 4H' },
  'liq.oi24h': { nl: 'OI 24U', en: 'OI 24H' },
  'liq.funding': { nl: 'FUNDING', en: 'FUNDING' },
  'liq.oiTrend': { nl: 'OI TREND', en: 'OI TREND' },
  'liq.liqZones': { nl: 'LIQUIDATIE ZONES', en: 'LIQUIDATION ZONES' },
  'liq.priceLadder': { nl: 'PRIJS LADDER', en: 'PRICE LADDER' },
  'liq.noZones': { nl: 'Geen zones beschikbaar', en: 'No zones available' },
  'liq.currentPrice': { nl: 'HUIDIGE PRIJS', en: 'CURRENT PRICE' },
  'liq.riskAnalysis': { nl: 'RISICO ANALYSE', en: 'RISK ANALYSIS' },
  'liq.cascadeScore': { nl: 'CASCADE SCORE', en: 'CASCADE SCORE' },
  'liq.recent24h': { nl: 'RECENTE LIQUIDATIES 24U', en: 'RECENT LIQUIDATIONS 24H' },
  'liq.longVsShort': { nl: 'LONG VS SHORT', en: 'LONG VS SHORT' },
  'liq.longsLiquidated': { nl: 'LONGS GELIQUIDEERD', en: 'LONGS LIQUIDATED' },
  'liq.shortsLiquidated': { nl: 'SHORTS GELIQUIDEERD', en: 'SHORTS LIQUIDATED' },
  'liq.aiIntelligence': { nl: 'AI INTELLIGENTIE', en: 'AI INTELLIGENCE' },
  'liq.magnetPrice': { nl: 'MAGNEET PRIJS', en: 'MAGNET PRICE' },
  'liq.direction': { nl: 'RICHTING', en: 'DIRECTION' },
  'liq.longZones': { nl: 'LONG ZONES', en: 'LONG ZONES' },
  'liq.shortZones': { nl: 'SHORT ZONES', en: 'SHORT ZONES' },
  'liq.cascadeHigh': { nl: 'HOOG', en: 'HIGH' },
  'liq.cascadeMedium': { nl: 'GEMIDDELD', en: 'MEDIUM' },
  'liq.cascadeLow': { nl: 'LAAG', en: 'LOW' },
  'liq.exchangeHeatmap': { nl: 'EXCHANGE LIQUIDATIE HEATMAP', en: 'EXCHANGE LIQUIDATION HEATMAP' },
  'liq.exchangeDominant': { nl: 'DOMINANT', en: 'DOMINANT' },
  'liq.exchangeShare': { nl: 'MARKTAANDEEL', en: 'MARKET SHARE' },
  'liq.long': { nl: 'LONG', en: 'LONG' },
  'liq.short': { nl: 'SHORT', en: 'SHORT' },
  'liq.orderWalls': { nl: 'ORDER WALL DETECTOR', en: 'ORDER WALL DETECTOR' },
  'liq.bidWalls': { nl: 'BID WALLS (BESCHERMING)', en: 'BID WALLS (PROTECTION)' },
  'liq.askWalls': { nl: 'ASK WALLS (PLAFOND)', en: 'ASK WALLS (CEILING)' },
  'liq.noWalls': { nl: 'Geen grote order walls gedetecteerd', en: 'No large order walls detected' },
  'liq.maxPainConvergence': { nl: 'MAX PAIN CONVERGENTIE', en: 'MAX PAIN CONVERGENCE' },
  'liq.optionsMaxPain': { nl: 'OPTIONS MAX PAIN', en: 'OPTIONS MAX PAIN' },
  'liq.liqMaxPain': { nl: 'LIQ MAX PAIN', en: 'LIQ MAX PAIN' },
  'liq.convergenceZone': { nl: 'CONVERGENTIE ZONE', en: 'CONVERGENCE ZONE' },
  'liq.divergent': { nl: 'DIVERGENT', en: 'DIVERGENT' },
  'liq.converging': { nl: 'CONVERGEREND', en: 'CONVERGING' },
  'liq.trapDetector': { nl: 'TRAP DETECTOR', en: 'TRAP DETECTOR' },
  'liq.trapWarning': { nl: 'WAARSCHUWING: Mogelijke val gedetecteerd', en: 'WARNING: Potential trap detected' },
  'liq.noTraps': { nl: 'Geen verdachte patronen gedetecteerd', en: 'No suspicious patterns detected' },
  'liq.wallAt': { nl: 'Wall op', en: 'Wall at' },
  'liq.liqZoneAt': { nl: 'Liq zone op', en: 'Liq zone at' },
  'liq.protection': { nl: 'BESCHERMING', en: 'PROTECTION' },
  'liq.ceiling': { nl: 'PLAFOND', en: 'CEILING' },

  'deriv.title': { nl: 'DERIVATEN LAB', en: 'DERIVATIVES LAB' },
  'deriv.subtitle': { nl: 'REAL-TIME DERIVATEN ANALYSE', en: 'REAL-TIME DERIVATIVES ANALYSIS' },
  'deriv.kernpunt': { nl: 'KERNPUNT', en: 'KEY POINT' },
  'deriv.pressure': { nl: 'DRUK', en: 'PRESSURE' },
  'deriv.positionData': { nl: 'POSITIE DATA', en: 'POSITION DATA' },
  'deriv.liqOptions': { nl: 'LIQUIDATIE & OPTIES', en: 'LIQUIDATION & OPTIONS' },
  'deriv.macroCycle': { nl: 'MACRO & CYCLUS', en: 'MACRO & CYCLE' },
  'deriv.extremeFunding': { nl: 'EXTREME FUNDING GEDETECTEERD', en: 'EXTREME FUNDING DETECTED' },
  'deriv.optionsExpiry': { nl: 'OPTIES EXPIRY', en: 'OPTIONS EXPIRY' },
  'deriv.nearestExpiry': { nl: 'DICHTSTE EXPIRY', en: 'NEAREST EXPIRY' },
  'deriv.largestExpiry': { nl: 'GROOTSTE EXPIRY', en: 'LARGEST EXPIRY' },
  'deriv.weightedMaxPain': { nl: 'GEWOGEN MAX PAIN', en: 'WEIGHTED MAX PAIN' },
  'deriv.datum': { nl: 'DATUM', en: 'DATE' },
  'deriv.cycleIndicators': { nl: 'CYCLUS INDICATOREN', en: 'CYCLE INDICATORS' },
  'deriv.crossed': { nl: 'GEKRUIST', en: 'CROSSED' },
  'deriv.yes': { nl: 'JA', en: 'YES' },
  'deriv.no': { nl: 'NEE', en: 'NO' },
  'deriv.estDaysToTop': { nl: 'EST. DAGEN TOT TOP', en: 'EST. DAYS TO TOP' },
  'deriv.topReached': { nl: 'TOP BEREIKT', en: 'TOP REACHED' },
  'deriv.weekTotal': { nl: 'WEEK TOTAAL', en: 'WEEK TOTAL' },
  'deriv.totalAssets': { nl: 'TOTALE ASSETS', en: 'TOTAL ASSETS' },
  'deriv.aiIntelligence': { nl: 'AI INTELLIGENTIE', en: 'AI INTELLIGENCE' },
  'deriv.poolIntelligence': { nl: 'POOL INTELLIGENCE', en: 'POOL INTELLIGENCE' },
  'deriv.crossExDivergence': { nl: 'CROSS-EX DIVERGENTIE', en: 'CROSS-EX DIVERGENCE' },
  'deriv.orderflow': { nl: 'ORDERFLOW', en: 'ORDERFLOW' },
  'deriv.microstructure': { nl: 'MICROSTRUCTUUR', en: 'MICROSTRUCTURE' },
  'deriv.openInterest': { nl: 'OPEN INTEREST', en: 'OPEN INTEREST' },
  'deriv.fundingRate': { nl: 'FUNDING RATE', en: 'FUNDING RATE' },
  'deriv.longShortRatio': { nl: 'LONG / SHORT RATIO', en: 'LONG / SHORT RATIO' },
  'deriv.liquidationOverview': { nl: 'LIQUIDATIE OVERZICHT', en: 'LIQUIDATION OVERVIEW' },
  'deriv.optionsIntelligence': { nl: 'OPTIES INTELLIGENTIE', en: 'OPTIONS INTELLIGENCE' },
  'deriv.marketSentiment': { nl: 'MARKT SENTIMENT', en: 'MARKET SENTIMENT' },
  'deriv.etfFlows': { nl: 'ETF FLOWS', en: 'ETF FLOWS' },
  'deriv.smartVsRetail': { nl: 'SMART vs RETAIL', en: 'SMART vs RETAIL' },
  'deriv.orderbookPressure': { nl: 'ORDERBOOK DRUK', en: 'ORDERBOOK PRESSURE' },
  'deriv.squeezeAlert': { nl: 'SQUEEZE RISICO', en: 'SQUEEZE RISK' },
  'deriv.squeezeDetected': { nl: 'SQUEEZE RISICO GEDETECTEERD', en: 'SQUEEZE RISK DETECTED' },
  'deriv.smartSignals': { nl: 'SMART SIGNALEN', en: 'SMART SIGNALS' },
  'deriv.topTraders': { nl: 'TOP TRADERS', en: 'TOP TRADERS' },
  'deriv.retailTraders': { nl: 'RETAIL', en: 'RETAIL' },
  'deriv.divergence': { nl: 'DIVERGENTIE', en: 'DIVERGENCE' },
  'deriv.buyers': { nl: 'KOPERS', en: 'BUYERS' },
  'deriv.sellers': { nl: 'VERKOPERS', en: 'SELLERS' },
  'deriv.balanced': { nl: 'GEBALANCEERD', en: 'BALANCED' },
  'deriv.oiWeightedFunding': { nl: 'OI-GEWOGEN FUNDING', en: 'OI-WEIGHTED FUNDING' },
  'deriv.coinbasePremium': { nl: 'COINBASE PREMIUM', en: 'COINBASE PREMIUM' },
  'deriv.cgdiIndex': { nl: 'CGDI INDEX', en: 'CGDI INDEX' },
  'deriv.cdriIndex': { nl: 'CDRI INDEX', en: 'CDRI INDEX' },
  'deriv.derivHealth': { nl: 'DERIVATEN GEZONDHEID', en: 'DERIVATIVES HEALTH' },
  'deriv.usSignal': { nl: 'US SIGNAAL', en: 'US SIGNAL' },
  'deriv.usBuying': { nl: 'US koopdruk', en: 'US buying pressure' },
  'deriv.usSelling': { nl: 'US verkoopdruk', en: 'US selling pressure' },
  'deriv.usNeutral': { nl: 'US neutraal', en: 'US neutral' },
  'deriv.fundingArbitrage': { nl: 'FUNDING ARBITRAGE', en: 'FUNDING ARBITRAGE' },

  'chart.tooltip.liq': { nl: 'Waar worden traders geliquideerd?', en: 'Where are traders getting liquidated?' },
  'chart.tooltip.sr': { nl: 'Waar kaatst de prijs?', en: 'Where does price bounce?' },
  'chart.tooltip.mc': { nl: 'Waar gaat de prijs waarschijnlijk heen?', en: 'Where is price likely heading?' },
  'chart.tooltip.ai': { nl: 'Wat denken 15+ AI engines?', en: 'What do 15+ AI engines think?' },
  'chart.tooltip.scen': { nl: "Welke scenario's zijn mogelijk?", en: 'What scenarios are possible?' },
  'chart.tooltip.macro': { nl: 'Macro regime, whales, opties, cascade risico en engine synergy', en: 'Macro regime, whales, options, cascade risk and engine synergy' },

  'chart.regime.trending_up': { nl: 'stijgende trend', en: 'bullish trend' },
  'chart.regime.trending_down': { nl: 'dalende trend', en: 'bearish trend' },
  'chart.regime.accumulation': { nl: 'accumulatie (slim geld koopt in)', en: 'accumulation (smart money buying)' },
  'chart.regime.distribution': { nl: 'distributie (slim geld verkoopt)', en: 'distribution (smart money selling)' },
  'chart.regime.mean_reversion': { nl: 'zijwaarts / range-gebonden', en: 'sideways / range-bound' },
  'chart.regime.high_volatility': { nl: 'hoge volatiliteit', en: 'high volatility' },

  'chart.overlay.liqNear.long': { nl: 'Waarschuwing: Er liggen grote long-liquidaties op', en: 'Warning: Large long liquidations clustered at' },
  'chart.overlay.liqNear.short': { nl: 'Waarschuwing: Er liggen grote short-liquidaties op', en: 'Warning: Large short liquidations clustered at' },
  'chart.overlay.liqSpread': {
    nl: 'Liquidatieniveaus zijn verspreid — geen direct cascade-risico. De prijs beweegt vrij zonder sterke magneetwerking.',
    en: 'Liquidation levels are spread out — no immediate cascade risk. Price moves freely without strong magnet effect.',
  },
  'chart.overlay.magnet': { nl: 'Magneet', en: 'Magnet' },
  'chart.overlay.magnetDesc': { nl: 'de prijs wordt hierheen getrokken', en: 'price is being pulled towards this level' },
  'chart.overlay.support': { nl: 'steun', en: 'support' },
  'chart.overlay.resistance': { nl: 'weerstand', en: 'resistance' },
  'chart.overlay.vwapAbove': { nl: 'erboven (positief)', en: 'above (bullish)' },
  'chart.overlay.vwapBelow': { nl: 'eronder (negatief)', en: 'below (bearish)' },
  'chart.overlay.squeezeActive': { nl: 'Bollinger squeeze actief', en: 'Bollinger squeeze active' },
  'chart.overlay.breakoutExpected': { nl: 'uitbraak verwacht', en: 'breakout expected' },
  'chart.overlay.aiAnalyzing': { nl: '15+ AI engines analyseren de markt continu. Regime wordt bepaald.', en: '15+ AI engines analyzing the market continuously. Regime being determined.' },
  'chart.overlay.deathZone': { nl: 'Waarschuwing: De AI heeft een Death Zone gedetecteerd', en: 'Warning: AI detected a Death Zone' },
  'chart.overlay.whalesBuying': { nl: 'kopen in (bullish)', en: 'buying (bullish)' },
  'chart.overlay.whalesSelling': { nl: 'verkopen (bearish)', en: 'selling (bearish)' },
  'chart.overlay.whales': { nl: 'Walvissen', en: 'Whales' },
  'chart.overlay.regimeSwitch': { nl: 'Regime wissel verwacht', en: 'Regime switch expected' },
  'chart.overlay.stableMarket': { nl: 'Marktcondities zijn stabiel', en: 'Market conditions are stable' },
  'chart.overlay.scenarioAnalysis': {
    nl: 'Scenario-analyse berekent meerdere toekomstige paden met waarschijnlijkheden.',
    en: 'Scenario analysis calculates multiple future paths with probabilities.',
  },
  'chart.overlay.targetPrice': { nl: 'Doelprijs', en: 'Target price' },

  'vizier.brein.thinking': { nl: 'markt doorrekenen...', en: 'analyzing market...' },
  'vizier.brein.insights': { nl: 'inzichten uit', en: 'insights from' },
  'vizier.brein.layers': { nl: 'analyselagen', en: 'analysis layers' },
  'vizier.action': { nl: 'ACTIE', en: 'ACTION' },

  'chart.label.regime': { nl: 'REGIME', en: 'REGIME' },
  'chart.label.whales': { nl: 'WHALES', en: 'WHALES' },
  'chart.label.maxPain': { nl: 'MAX PAIN', en: 'MAX PAIN' },
  'chart.label.cascade': { nl: 'CASCADE', en: 'CASCADE' },
  'chart.label.synergy': { nl: 'SYNERGY', en: 'SYNERGY' },

  'vizier.analist': { nl: 'Analist', en: 'Analyst' },
  'vizier.nieuw': { nl: 'NIEUW', en: 'NEW' },

  'vizier.urg.kritiek': { nl: 'KRITIEK', en: 'CRITICAL' },
  'vizier.urg.hoog': { nl: 'HOOG', en: 'HIGH' },
  'vizier.urg.middel': { nl: 'MIDDEL', en: 'MEDIUM' },
  'vizier.urg.laag': { nl: 'LAAG', en: 'LOW' },

  'vizier.cat.squeeze_detectie': { nl: 'Squeeze Detectie', en: 'Squeeze Detection' },
  'vizier.cat.risico_alert': { nl: 'Risico Alert', en: 'Risk Alert' },
  'vizier.cat.uitbraak_setup': { nl: 'Uitbraak Setup', en: 'Breakout Setup' },
  'vizier.cat.contrair_signaal': { nl: 'Contrair Signaal', en: 'Contrarian Signal' },
  'vizier.cat.distributie_risico': { nl: 'Distributie Risico', en: 'Distribution Risk' },
  'vizier.cat.kritiek_risico': { nl: 'Kritiek Risico', en: 'Critical Risk' },
  'vizier.cat.uitbraak_predictie': { nl: 'Uitbraak Predictie', en: 'Breakout Prediction' },
  'vizier.cat.onzekerheid': { nl: 'Onzekerheid', en: 'Uncertainty' },
  'vizier.cat.verborgen_signaal': { nl: 'Verborgen Signaal', en: 'Hidden Signal' },
  'vizier.cat.convergentie': { nl: 'Convergentie', en: 'Convergence' },
  'vizier.cat.tail_risk': { nl: 'Tail Risk', en: 'Tail Risk' },
  'vizier.cat.cyclus_signaal': { nl: 'Cyclus Signaal', en: 'Cycle Signal' },
  'vizier.cat.cyclus_waarschuwing': { nl: 'Cyclus Waarschuwing', en: 'Cycle Warning' },
  'vizier.cat.regime_shift': { nl: 'Regime Shift', en: 'Regime Shift' },
  'vizier.cat.volatiliteit_setup': { nl: 'Volatiliteit Setup', en: 'Volatility Setup' },
  'vizier.cat.consensus_kracht': { nl: 'Consensus Kracht', en: 'Consensus Strength' },
  'vizier.cat.multi_layer_confluency': { nl: 'Multi-Layer Confluency', en: 'Multi-Layer Confluency' },
  'vizier.cat.magneet_convergentie': { nl: 'Magneet Convergentie', en: 'Magnet Convergence' },
  'vizier.cat.magneet_tracking': { nl: 'Magneet Tracking', en: 'Magnet Tracking' },
  'vizier.cat.target_analyse': { nl: 'Target Analyse', en: 'Target Analysis' },
  'vizier.cat.cascade_risico': { nl: 'Cascade Risico', en: 'Cascade Risk' },
  'vizier.cat.proximity_alert': { nl: 'Proximity Alert', en: 'Proximity Alert' },
  'vizier.cat.contrair_opties': { nl: 'Contrair Opties', en: 'Contrarian Options' },
  'vizier.cat.whale_intelligence': { nl: 'Whale Intelligence', en: 'Whale Intelligence' },
  'vizier.cat.divergentie_signaal': { nl: 'Divergentie Signaal', en: 'Divergence Signal' },
  'vizier.cat.momentum_shift': { nl: 'Momentum Shift', en: 'Momentum Shift' },
  'vizier.cat.momentum_acceleratie': { nl: 'Momentum Acceleratie', en: 'Momentum Acceleration' },
  'vizier.cat.exchange_analyse': { nl: 'Exchange Analyse', en: 'Exchange Analysis' },
  'vizier.cat.institutionele_setup': { nl: 'Institutionele Setup', en: 'Institutional Setup' },
  'vizier.cat.range_analyse': { nl: 'Range Analyse', en: 'Range Analysis' },
  'vizier.cat.scenario_tracker': { nl: 'Scenario Tracker', en: 'Scenario Tracker' },
  'vizier.cat.scenario_conflict': { nl: 'Scenario Conflict', en: 'Scenario Conflict' },
  'vizier.cat.regime_transitie': { nl: 'Regime Transitie', en: 'Regime Transition' },
  'vizier.cat.sentiment_extreme': { nl: 'Sentiment Extreme', en: 'Sentiment Extreme' },
  'vizier.cat.engine_unanimiteit': { nl: 'Engine Unanimiteit', en: 'Engine Unanimity' },
  'vizier.cat.engine_dominantie': { nl: 'Engine Dominantie', en: 'Engine Dominance' },
  'vizier.cat.data_divergentie': { nl: 'Data Divergentie', en: 'Data Divergence' },
  'vizier.cat.intern_conflict': { nl: 'Intern Conflict', en: 'Internal Conflict' },
  'vizier.cat.consensus_extreme': { nl: 'Consensus Extreme', en: 'Consensus Extreme' },
  'vizier.cat.weerstand_analyse': { nl: 'Weerstand Analyse', en: 'Resistance Analysis' },
  'vizier.cat.structureel_niveau': { nl: 'Structureel Niveau', en: 'Structural Level' },
  'vizier.cat.stealth_detectie': { nl: 'Stealth Detectie', en: 'Stealth Detection' },
  'vizier.cat.stille_uitbraak': { nl: 'Stille Uitbraak', en: 'Silent Breakout' },
  'vizier.cat.chirurgische_trade': { nl: 'Chirurgische Trade', en: 'Surgical Trade' },
  'vizier.cat.super_confluency': { nl: 'Super Confluency', en: 'Super Confluency' },
  'vizier.cat.extreme_confluency': { nl: 'Extreme Confluency', en: 'Extreme Confluency' },
  'vizier.cat.trigger_cascade': { nl: 'Trigger Cascade', en: 'Trigger Cascade' },
  'vizier.cat.katalysator_alert': { nl: 'Katalysator Alert', en: 'Catalyst Alert' },
  'vizier.cat.simulatie_analyse': { nl: 'Simulatie Analyse', en: 'Simulation Analysis' },
  'vizier.cat.euforie_waarschuwing': { nl: 'Euforie Waarschuwing', en: 'Euphoria Warning' },
  'vizier.cat.temperatuur_alert': { nl: 'Temperatuur Alert', en: 'Temperature Alert' },
  'vizier.cat.kwaliteitswaarschuwing': { nl: 'Kwaliteitswaarschuwing', en: 'Quality Warning' },
  'vizier.cat.deleveraging_event': { nl: 'Deleveraging Event', en: 'Deleveraging Event' },
  'vizier.cat.distributie_detectie': { nl: 'Distributie Detectie', en: 'Distribution Detection' },
  'vizier.cat.on_chain_fundament': { nl: 'On-Chain Fundament', en: 'On-Chain Fundamental' },
  'vizier.cat.flow_confluency': { nl: 'Flow Confluency', en: 'Flow Confluency' },
  'vizier.cat.volume_confluency': { nl: 'Volume Confluency', en: 'Volume Confluency' },
  'vizier.cat.ai_conflictsignaal': { nl: 'AI Conflictsignaal', en: 'AI Conflict Signal' },
  'vizier.cat.ai_convergentie': { nl: 'AI Convergentie', en: 'AI Convergence' },
  'vizier.cat.verborgen_divergentie': { nl: 'Verborgen Divergentie', en: 'Hidden Divergence' },
  'vizier.cat.dubbele_bevestiging': { nl: 'Dubbele Bevestiging', en: 'Double Confirmation' },
  'vizier.cat.dubbele_trend': { nl: 'Dubbele Trend', en: 'Double Trend' },
  'vizier.cat.triple_oscillator': { nl: 'Triple Oscillator', en: 'Triple Oscillator' },
  'vizier.cat.triple_trend': { nl: 'Triple Trend', en: 'Triple Trend' },
  'vizier.cat.multi_tf_overbought': { nl: 'Multi-TF Overbought', en: 'Multi-TF Overbought' },
  'vizier.cat.multi_tf_oversold': { nl: 'Multi-TF Oversold', en: 'Multi-TF Oversold' },
  'vizier.cat.ichimoku_confluency': { nl: 'Ichimoku Confluency', en: 'Ichimoku Confluency' },
  'vizier.cat.cloud_squeeze': { nl: 'Cloud Squeeze', en: 'Cloud Squeeze' },
  'vizier.cat.keltner_alert': { nl: 'Keltner Alert', en: 'Keltner Alert' },
  'vizier.cat.elder_ray_analyse': { nl: 'Elder Ray Analyse', en: 'Elder Ray Analysis' },
  'vizier.cat.ema_alignment': { nl: 'EMA Alignment', en: 'EMA Alignment' },
  'vizier.cat.aroon_trend': { nl: 'Aroon Trend', en: 'Aroon Trend' },
  'vizier.cat.pivot_analyse': { nl: 'Pivot Analyse', en: 'Pivot Analysis' },
  'vizier.cat.markov_analyse': { nl: 'Markov Analyse', en: 'Markov Analysis' },
  'vizier.cat.wyckoff_analyse': { nl: 'Wyckoff Analyse', en: 'Wyckoff Analysis' },

  'header.stable': { nl: 'stabiel', en: 'stable' },
  'header.regime.bearish': { nl: 'Dalend', en: 'Bearish' },
  'header.regime.bullish': { nl: 'Stijgend', en: 'Bullish' },
  'header.regime.transition': { nl: 'Transitie', en: 'Transition' },
  'header.regime.sideways': { nl: 'Zijwaarts', en: 'Sideways' },
  'header.regime.squeeze': { nl: 'Spanning', en: 'Squeeze' },
  'header.regime.accumulation': { nl: 'Opbouw', en: 'Accumulation' },
  'header.regime.distribution': { nl: 'Afbouw', en: 'Distribution' },
  'header.regime.volatile': { nl: 'Beweeglijk', en: 'Volatile' },
  'header.regime.analyzing': { nl: 'Analyseren', en: 'Analyzing' },
  'header.regime.loading': { nl: 'Laden', en: 'Loading' },
  'header.horizon.short': { nl: 'Kort', en: 'Short' },
  'header.horizon.mid': { nl: 'Midden', en: 'Mid' },
  'header.horizon.long': { nl: 'Lang', en: 'Long' },
  'header.dir.positive': { nl: 'Positief', en: 'Bullish' },
  'header.dir.negative': { nl: 'Negatief', en: 'Bearish' },
  'header.dir.neutral': { nl: 'Neutraal', en: 'Neutral' },
  'header.langToggle.nl': { nl: 'Switch to English', en: 'Switch to English' },
  'header.langToggle.en': { nl: 'Schakel naar Nederlands', en: 'Schakel naar Nederlands' },
};

export function useT() {
  const { lang } = useLang();
  return useCallback(
    (key: string, fallback?: string): string => {
      const entry = T[key];
      if (entry) return entry[lang];
      return fallback || key;
    },
    [lang],
  );
}

export function tStatic(key: string, lang: Lang, fallback?: string): string {
  const entry = T[key];
  if (entry) return entry[lang];
  return fallback || key;
}
